import _ from 'lodash';
import { createSelector } from 'reselect';

import { serviceNames, statuses } from '../../constants/project';
import { getRouteParams } from '../../router/router.selectors';
import { getSelectedOrganizationId } from '../../selectors/organizations/organizations';
import {
  Project,
  ProjectInOrganizationId,
  ProjectService,
  ProjectStatus,
} from '../../types/entities';
import { MaybeUndef } from '../../types/system';
import {
  getActiveProjectService,
  getProjectImageService,
} from '../../utils/projects/projects';
import { StoreState } from '../reducers/store.types';

export const getRawProjects = (state) => state.projects;

export const getProjects = (state: StoreState): Project[] => state.projects;

export const getProjectIdFromRoute = createSelector(
  getRouteParams,
  (routeParams): MaybeUndef<string> => _.get(routeParams, 'projectId'),
);

export const getNotDeletingProjects = createSelector(
  getProjects,
  (projects: Project[]): Project[] =>
    _.filter(projects, ({ status }) => status !== ProjectStatus.DELETING),
);

export const getDeletingProjects = createSelector(
  getProjects,
  (projects: Project[]): Project[] =>
    _.filter(projects, { status: ProjectStatus.DELETING }),
);

export const getPendingProjects = createSelector(
  getProjects,
  (projects: Project[]): Project[] =>
    _.filter(projects, { status: ProjectStatus.PENDING }),
);

export const getReadyProjects = createSelector(
  getProjects,
  (projects: Project[]): Project[] =>
    _.filter(projects, { status: ProjectStatus.READY }),
);

export const getProjectsSelectedProject = createSelector(
  getProjects,
  getRouteParams,
  (projects, routeParams) =>
    _.find(projects, { id: routeParams.projectsProjectId }),
);

export const getSelectedProject = createSelector(
  getProjects,
  getRouteParams,
  (projects, routeParams) =>
    _.find<Project>(projects, { id: _.get(routeParams, 'projectId') }),
);

export const getProjectByRoute = createSelector(
  getProjects,
  getRouteParams,
  (projects, routeParams): Project => {
    const id = _.get(routeParams, 'projectId');

    if (!id) {
      throw new Error(
        `Can't get project by route. Project id doesn't exist in url`,
      );
    }

    const project: MaybeUndef<Project> = _.find<Project>(projects, { id });

    if (!project) {
      throw new Error(
        `Can't get project by route. Project ${id} doesn't exist or not yet loaded`,
      );
    }

    return project;
  },
);

export const getSelectedProjectId = createSelector(
  getSelectedProject,
  (project: Project): string => _.get(project, 'id'),
);

export const getSelectedProjectName = createSelector(
  getSelectedProject,
  (project: Project): string => _.get(project, 'name', ''),
);

export const getProjectNameById = createSelector(getProjects, (projects) =>
  _.memoize((projectId: string) => {
    const project = _.find(projects, { id: projectId });
    return _.get(project, 'name');
  }),
);

const getFirstProject = createSelector(
  getNotDeletingProjects,
  (projects: Project[]): MaybeUndef<Project> => _.head(projects),
);

// get project from url or first one in list
export const getDefaultProject = createSelector(
  getSelectedProject,
  getFirstProject,
  (
    selectedProject: MaybeUndef<Project>,
    firstProject: MaybeUndef<Project>,
  ): Project | undefined => selectedProject || firstProject,
);

export const getDefaultProjectId = createSelector(
  getRouteParams,
  getDefaultProject,
  (routeParams, project): string =>
    _.get(project, 'id') || _.get(routeParams, 'projectId'),
);

export const getCurrentFileService = createSelector(
  getDefaultProject,
  (project): ProjectService => {
    const res =
      project && _.find(project.services, { name: serviceNames.file });

    // TODO: handle undefined in all consumers of getCurrentFileService
    return res as ProjectService;
  },
);

export const getCurrentImageService = createSelector(
  getDefaultProject,
  getProjectImageService,
);

export const getCurrentVideoPackagerService = createSelector(
  getDefaultProject,
  (project) =>
    project && _.find(project.services, { name: serviceNames.videoPackager }),
);

export const getCurrentActiveImageService = createSelector(
  getCurrentImageService,
  getActiveProjectService,
);

export const getCurrentActiveVideoPackagerService = createSelector(
  getCurrentVideoPackagerService,
  getActiveProjectService,
);

export const isCurrentProjectReady = createSelector(
  getDefaultProject,
  (project: Project): boolean => project && project.status === statuses.READY,
);

export const getProjectScopeByRoute = createSelector(
  getSelectedOrganizationId,
  getProjectByRoute,
  (organizationId: string, project: Project): ProjectInOrganizationId => ({
    organizationId,
    project,
  }),
);
